// Per-Block inter-Block padding registry (P-037.29 / D-137 §item 2).
// Consumed by `renderEmail` to emit a transparent spacer row between
// successive Blocks; the spacer's height = `bottom(N) + top(N+1)`.
//
// **Why a system-level spacer instead of per-Block outer padding?**
// Tinted Blocks (`footer`, `big3-signature-events`, `time-location-
// cost`) emit `bgcolor=ctx.tintColor` on their outer table. If the
// inter-Block gap lived INSIDE the tinted cell as padding-bottom, the
// tinted color would extend into the gap (visible "tint shelf"). With
// the system-level spacer, tinted bg ends at the Block's content
// edge; the spacer between Blocks is transparent. Matches Banks's
// "30 px after the bottom of the tinted box" intent.
//
// **Convention** (Banks-confirmed at planning):
//
//   | Block category        | Top | Bottom |
//   |-----------------------|-----|--------|
//   | HEADER                | 0   | 30     |
//   | CONTENT (all 13)      | 0   | 30     |
//   | FOOTER                | 30  | 0      |
//   | META (default)        | 0   | 30     |
//   |   `section-heading`   | 20  | 50     |
//   |   `sub-section-heading` | 0 | 20     |
//   |   `issue-date`        | 30  | 30     |
//
// Inter-Block gap = `bottom(N) + top(N+1)`. Examples:
//   - Header → Issue-date: 30 + 30 = 60 px → 30 px (P-037.30 sixth
//     post-Phase-Close §A `header→issue-date` PAIR_OVERRIDES entry)
//   - Content → Content: 30 + 0 = 30 px
//   - Content → section-heading: 30 + 20 = 50 px (sits above rule line)
//   - section-heading → Content: 50 + 0 = 50 px (D-117 50 px rhythm)
//   - sub-section-heading → Content: 20 + 0 = 20 px
//   - Last Content → Footer: 30 + 30 = 60 px (P-037.30 eighth post-
//     Phase-Close §B bumped footer.top 0 → 30 — Banks's preview-walk
//     wanted more breathing room above the tinted footer panel).
//
// Footer's bottom=0 is "terminal" — the navy bar stays flush at the
// email's bottom edge (no next Block to gap to). `renderEmail`
// suppresses the trailing spacer after the last Block regardless.

import type { BlockCategory } from '../types'

export interface BlockPadding {
  readonly top: number
  readonly bottom: number
}

export const BLOCK_PADDING: Readonly<Record<BlockCategory, BlockPadding>> = Object.freeze({
  header: { top: 0, bottom: 30 },
  content: { top: 0, bottom: 30 },
  // P-037.30 eighth post-Phase-Close §B — footer.top 0 → 30. Banks's
  // preview-walk wanted more breathing room above the tinted footer
  // panel (the `<table bgcolor=ctx.tintColor>` in `src/blocks/footer.ts`
  // starts immediately at the top of the Block's render output). With
  // top=30, Content → Footer system spacer fires as `30 (content.bottom)
  // + 30 (footer.top) = 60 px` instead of the prior 30. footer.bottom
  // stays at 0 — the navy bar still sits flush at the email's bottom
  // edge, and `renderEmail` suppresses the trailing spacer after the
  // last Block regardless.
  footer: { top: 30, bottom: 0 },
  meta: { top: 0, bottom: 30 },
})

/**
 * Per-`block.id` overrides for META Blocks. Looked up after
 * `BLOCK_PADDING.meta` — any META Block whose id appears here gets
 * the override; unmapped META Blocks fall through to the category
 * default (`{top: 0, bottom: 30}`).
 *
 * HEADER, CONTENT, and FOOTER categories use the category default only;
 * no per-id override surface today (extend the lookup if a category
 * ever needs per-id rhythm).
 */
export const META_PADDING_OVERRIDES: Readonly<Record<string, BlockPadding>> = Object.freeze({
  // P-037.29 post-Phase-Close §item B — section-heading top dropped
  // 20 → 0; the 20 px above the rule moved INSIDE the SectionHeader
  // Component so it shows in Block Gallery isolated previews too
  // (the system spacer here only fires between successive Blocks).
  // Bottom stays at 50 — system spacer owns the heading↔body gap.
  'section-heading': { top: 0, bottom: 50 },
  'sub-section-heading': { top: 0, bottom: 20 },
  'issue-date': { top: 30, bottom: 30 },
  // P-037.30 sixth post-Phase-Close §B — Spacer Block's category
  // padding zeroed on BOTH sides so the spacer's own rendered content
  // (30/60/90 px transparent `<td height>` row) IS the contributed
  // visible space, with no extra padding from the META default's
  // `{top: 0, bottom: 30}`. Surrounding system gaps still fire
  // normally per the ADD semantic Banks picked — picking `'30'` adds
  // 30 px ON TOP OF the prev/next Block's normal inter-Block gap.
  // Worked example: Content→Spacer(30)→Content = 30 (content.bottom)
  // + 0 (spacer.top) + 30 (spacer content) + 0 (spacer.bottom) + 0
  // (content.top) = 60 px total. Without this override, the same
  // pair would compute as 30 + 0 + 30 + 30 + 0 = 90 px (the META
  // default's 30 px bottom would double up on the gap).
  spacer: { top: 0, bottom: 0 },
})

/**
 * Resolve the inter-Block padding for a Block instance. META Blocks
 * consult `META_PADDING_OVERRIDES` first; others use the category
 * default.
 */
export function blockPadding(category: BlockCategory, blockId: string): BlockPadding {
  if (category === 'meta') {
    return META_PADDING_OVERRIDES[blockId] ?? BLOCK_PADDING.meta
  }
  return BLOCK_PADDING[category]
}

/**
 * P-037.31 fourth post-Phase-Close §D — per-pair overrides for the
 * inter-Block spacer. Keyed by `${prevBlockId}→${nextBlockId}` (using
 * the U+2192 RIGHTWARDS ARROW so the literal is unambiguous in source
 * + log diagnostics). Value is the fixed spacer height in px that
 * REPLACES the normal `bottom(prev) + top(next)` sum.
 *
 * Today: only `big3-signature-events → sub-section-heading` collapses
 * to 0. Banks's observation: the big-3 Block's outer table carries
 * `bgcolor=ctx.tintColor`, ending the Block with a tinted footer edge.
 * A trailing standalone `sub-section-heading` adds its own divider
 * rule + 30 px above-rule padding (via SubSectionHeader's `PAD_TOP`),
 * reading as TWO visual separators stacked (tint edge + rule). The
 * pair-override collapses the inter-Block gap AND `sub-section-
 * heading.ts` coordinately threads `hideTopRule: true` (via
 * `ctx.precedingBlockId === 'big3-signature-events'`) to drop the
 * heading's own rule — `PAD_TOP` 30 px stays so the heading text has
 * a gap below the tinted footer edge (P-037.30 ninth post-Phase-Close
 * §C — corrected from the prior `noTopRule: true` which dropped BOTH
 * rule + PAD_TOP and left the heading text flush against the tinted
 * edge).
 *
 * Scope is intentionally narrow per Banks's planning-session pick —
 * only this one pairing today. Future pair-overrides extend the
 * registry; the new pair-aware function below is the single seam.
 */
export const PAIR_OVERRIDES: Readonly<Record<string, number>> = Object.freeze({
  'big3-signature-events→sub-section-heading': 0,
  // P-037.30 sixth post-Phase-Close §A — Banks: "Lets reduce the
  // padding below the Heading block." Normal `header.bottom (30) +
  // issue-date.top (30) = 60` is too breathy for the typical Header
  // → "Month Year" issue-date pairing. The pair-override drops the
  // gap to 30 px (= `header.bottom` + an effective `issue-date.top`
  // of 0 for THIS pair only). Other Header → next-Block gaps stay
  // unchanged because PAIR_OVERRIDES is keyed by exact pair; Header
  // → Content stays at 30, Header → section-heading stays at 50,
  // Header → sub-section-heading stays at 30. Issue-date's `top: 30`
  // META default still fires for any non-Header predecessor — e.g.,
  // Content → issue-date (rare in practice) still gets 60.
  'header→issue-date': 30,
})

/**
 * Pair-aware inter-Block spacer height. Consults `PAIR_OVERRIDES`
 * first (keyed by `${prevId}→${nextId}`); falls back to the normal
 * `blockPadding(prev).bottom + blockPadding(next).top` sum. Consumed
 * by `renderEmail` (P-037.31 fourth post-Phase-Close §D — replaces
 * the prior inline math at the spacer emission site).
 */
export function interBlockSpacerHeight(
  prevCategory: BlockCategory,
  prevBlockId: string,
  nextCategory: BlockCategory,
  nextBlockId: string,
): number {
  const override = PAIR_OVERRIDES[`${prevBlockId}→${nextBlockId}`]
  if (override !== undefined) return override
  return blockPadding(prevCategory, prevBlockId).bottom + blockPadding(nextCategory, nextBlockId).top
}
